import type {
  DeviceType,
  PlayerLayer,
  ResolvedSetting,
  SettingDefinition,
  SettingLayers,
  SettingScope,
} from './settingTypes';

/**
 * The player layers a setting of this scope can hold, narrowest first. A mode
 * value falls back to the global value; an instance value falls back to the
 * mode default, then the global value.
 */
export const playerLayersFor = (scope: SettingScope): readonly PlayerLayer[] => {
  switch (scope) {
    case 'instance':
      return ['instance', 'mode', 'global'];
    case 'mode':
      return ['mode', 'global'];
    case 'global':
      return ['global'];
    default:
      throw new Error(`Unknown setting scope: ${String(scope)}`);
  }
};

type ResolvableDefinition<T> = Pick<
  SettingDefinition<T>,
  'scope' | 'precedence' | 'defaultValue' | 'isStricter'
> &
  Partial<Pick<SettingDefinition<T>, 'deviceDefaults'>>;

/** The built-in default, on this kind of device when the setting has one for it. */
export const builtInDefault = <T>(
  definition: Pick<SettingDefinition<T>, 'defaultValue'> &
    Partial<Pick<SettingDefinition<T>, 'deviceDefaults'>>,
  deviceType?: DeviceType,
): T => {
  const forDevice = deviceType ? definition.deviceDefaults?.[deviceType] : undefined;
  return forDevice === undefined ? definition.defaultValue : forDevice;
};

const firstPlayerValue = <T>(
  scope: SettingScope,
  layers: SettingLayers<T>,
): ResolvedSetting<T> | undefined =>
  playerLayersFor(scope)
    .filter((layer) => layers[layer] !== undefined)
    .map((layer) => ({ value: layers[layer] as T, source: layer }))[0];

/**
 * Which value wins.
 * - A lock wins over everything: a teacher or an accepted Brio plan locked the
 *   value on the node being played, so the player's value waits, as it does
 *   under authored clearance.
 * - Feel values: the player's narrowest value (this piece / exercise / session,
 *   then the mode default, then the global value), then the authored value,
 *   then the built-in default.
 * - Authored grading: a personal value never loosens an authored bar. It wins
 *   only where nothing is authored (filling the gap) or where the setting says
 *   it is stricter than the authored value (tightening, via `isStricter`).
 *
 * `deviceType` picks the built-in default for this kind of device, when the
 * setting has one.
 */
export const resolveSetting = <T>(
  definition: ResolvableDefinition<T>,
  layers: SettingLayers<T>,
  deviceType?: DeviceType,
): ResolvedSetting<T> => {
  if (layers.lock !== undefined) return { value: layers.lock.value, source: 'locked' };
  const fromPlayer = firstPlayerValue(definition.scope, layers);
  const fromAuthor: ResolvedSetting<T> | undefined =
    layers.authored === undefined ? undefined : { value: layers.authored, source: 'authored' };
  const fromDefault: ResolvedSetting<T> = {
    value: builtInDefault(definition, deviceType),
    source: 'default',
  };

  switch (definition.precedence) {
    case 'feel':
      return fromPlayer ?? fromAuthor ?? fromDefault;
    case 'authored_grading': {
      const tightens =
        fromPlayer !== undefined &&
        fromAuthor !== undefined &&
        definition.isStricter !== undefined &&
        definition.isStricter(fromPlayer.value, fromAuthor.value);
      return tightens ? fromPlayer : (fromAuthor ?? fromPlayer ?? fromDefault);
    }
    default:
      throw new Error(`Unknown setting precedence: ${String(definition.precedence)}`);
  }
};

/**
 * The layers a player's change writes. A feel value changed for one piece,
 * exercise or session also becomes the mode default, so the next one starts
 * where the player left off, unless the setting belongs to the piece alone
 * (`instanceChangeSetsModeDefault: false`). Every other change writes only its
 * own layer. The authored layer is never written by a player, and nothing is
 * written over a lock (`canPlayerChange`).
 */
export const layersToWrite = (
  definition: Pick<SettingDefinition<unknown>, 'key' | 'scope' | 'precedence'> &
    Partial<Pick<SettingDefinition<unknown>, 'instanceChangeSetsModeDefault'>>,
  layer: PlayerLayer,
): readonly PlayerLayer[] => {
  if (!playerLayersFor(definition.scope).includes(layer)) {
    throw new Error(
      `Setting ${definition.key} is ${definition.scope}-scoped and has no ${layer} value`,
    );
  }
  const setsModeDefault =
    definition.precedence === 'feel' && definition.instanceChangeSetsModeDefault !== false;
  return setsModeDefault && layer === 'instance' ? ['instance', 'mode'] : [layer];
};

/**
 * Whether the player may change a value: not while a teacher or an accepted
 * Brio plan has it locked. (Lowering a locked bar as a surfaced "below the
 * assignment" clear is a later rule; the lock here holds.)
 */
export const canPlayerChange = <T>(layers: Pick<SettingLayers<T>, 'lock'>): boolean =>
  layers.lock === undefined;
